import * as React from 'react';

import { TextField, Select } from '@rematter/ui-web';
import { Clock } from 'lucide-react';

import { ExpressionInput } from '../../../shared/ExpressionInput';
import { NodeConfigProps } from '../../types';

import { DelayConfigType, computeDurationSeconds } from './schema';

/**
 * Configuration panel for Delay nodes.
 * Allows configuring fixed duration or delay until specific datetime.
 */
export const DelayConfig: React.FC<NodeConfigProps<DelayConfigType>> = ({
  config,
  onUpdate,
}) => {
  const [delayType, setDelayType] = React.useState<string>(
    config.delayType || 'fixed',
  );
  const [duration, setDuration] = React.useState<string>(
    config.duration?.toString() || '5',
  );
  const [durationUnit, setDurationUnit] = React.useState<string>(
    config.durationUnit || 'minutes',
  );
  const [untilDateTime, setUntilDateTime] = React.useState<string>(
    config.untilDateTime || '',
  );
  const [untilExpression, setUntilExpression] = React.useState<string>(
    config.untilExpression || '',
  );

  const handleUpdate = React.useCallback(
    (updates: Partial<DelayConfigType>) => {
      // Save config as usual for UI state
      const newConfig = { ...config, ...updates };

      // Compute duration in seconds for backend
      const dur = updates.duration ?? config.duration ?? parseInt(duration, 10);
      const unit = updates.durationUnit ?? config.durationUnit ?? durationUnit;
      const durationSeconds = computeDurationSeconds(dur || 0, unit);

      // Pass both config and the computed duration
      // Note: The parent (NodeConfigPanel) will need to handle the duration field specially
      onUpdate({
        ...newConfig,
        // Store the computed duration for backend compatibility
        ...(newConfig.delayType === 'fixed' && {
          _computedDurationSeconds: durationSeconds,
        }),
      } as DelayConfigType);
    },
    [config, duration, durationUnit, onUpdate],
  );

  const handleDelayTypeChange = React.useCallback(
    (value: string) => {
      setDelayType(value);
      handleUpdate({ delayType: value as 'fixed' | 'until' });
    },
    [handleUpdate],
  );

  const handleDurationChange = React.useCallback(
    (value: string) => {
      setDuration(value);
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        handleUpdate({ duration: numValue });
      }
    },
    [handleUpdate],
  );

  const handleDurationUnitChange = React.useCallback(
    (value: string) => {
      setDurationUnit(value);
      handleUpdate({
        durationUnit: value as 'seconds' | 'minutes' | 'hours' | 'days',
      });
    },
    [handleUpdate],
  );

  const handleUntilDateTimeChange = React.useCallback(
    (value: string) => {
      setUntilDateTime(value);
      handleUpdate({ untilDateTime: value });
    },
    [handleUpdate],
  );

  const handleUntilExpressionChange = React.useCallback(
    (value: string) => {
      setUntilExpression(value);
      handleUpdate({ untilExpression: value });
    },
    [handleUpdate],
  );

  return (
    <div className="@DelayConfig space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium">Delay Type</label>
        <Select
          placeholder="Select delay type..."
          value={delayType}
          onChange={handleDelayTypeChange}
          options={[
            { key: 'fixed', label: 'Fixed Duration' },
            { key: 'until', label: 'Until DateTime' },
          ]}
        />
      </div>

      {delayType === 'fixed' ? (
        <div className="space-y-3">
          <TextField
            label="Duration"
            value={duration}
            onChange={handleDurationChange}
            placeholder="5"
            type="number"
            description="How long to wait before continuing"
          />

          <div>
            <label className="mb-1 block text-sm font-medium">Unit</label>
            <Select
              placeholder="Select time unit..."
              value={durationUnit}
              onChange={handleDurationUnitChange}
              options={[
                { key: 'seconds', label: 'Seconds' },
                { key: 'minutes', label: 'Minutes' },
                { key: 'hours', label: 'Hours' },
                { key: 'days', label: 'Days' },
              ]}
            />
          </div>

          {duration && durationUnit && (
            <div className="flex items-center gap-2 rounded-lg border bg-neutral/5 p-3">
              <Clock className="size-4 text-neutral/60" />
              <span className="text-sm text-neutral/70">
                Will wait for {duration} {durationUnit}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <TextField
            label="Until DateTime"
            value={untilDateTime}
            onChange={handleUntilDateTimeChange}
            placeholder="2024-12-31T23:59:59Z"
            type="datetime-local"
            description="Specific date and time to wait until"
          />

          <div className="pt-2">
            <ExpressionInput
              label="Or Use Expression"
              value={untilExpression}
              onChange={handleUntilExpressionChange}
              placeholder="{{trigger.data.scheduledDate}}"
              description="Dynamic datetime from trigger or previous actions"
            />
          </div>

          <div className="rounded-lg border border-warning/20 bg-warning/10 p-3">
            <p className="text-xs text-warning">
              If both are provided, the expression takes precedence over the
              static datetime.
            </p>
          </div>
        </div>
      )}

      <div className="rounded-lg border bg-neutral/5 p-3">
        <p className="mb-2 text-xs font-medium">Common Use Cases:</p>
        <div className="space-y-1 text-xs text-neutral/70">
          <div>• Wait 5 minutes before sending a reminder</div>
          <div>• Wait 24 hours before following up</div>
          <div>• Wait until a specific date for scheduled tasks</div>
          <div>• Wait until a dynamic date from trigger data</div>
        </div>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
        <p className="text-xs text-primary">
          <strong>Note:</strong> The automation will pause at this step and
          resume after the specified delay.
        </p>
      </div>
    </div>
  );
};
